import RadarZeroField from "components/calibration/RadarZeroField";
import { Field, SelectInput, UnitInput } from "components/forms/FormParts";

export const BENCH_PIPE_OPENING_CM = 90; // the bench pipe: 120 cm long, buried to its 30 cm mark
// The PadiHAT radar variation's sleeve: how far the hat reaches down over the pipe (PadiHAT bottom to pipe opening).
// Mirrors the backend default (Device.sleeve_overlap_cm); change both when the hat design changes.
export const SLEEVE_OVERLAP_DEFAULT_CM = 3.6;

const overlapOf = (form) => {
  const value = parseFloat(form.sleeve_overlap_cm);
  return Number.isFinite(value) ? value : SLEEVE_OVERLAP_DEFAULT_CM;
};

export const SENSOR_TYPE_OPTIONS = [
  { value: "auto", label: "Detect automatically" },
  { value: "radar", label: "Radar" },
  { value: "ultrasonic", label: "Ultrasonic" },
];

/** "radar" | "ultrasonic": the form's choice, or what the backend detected for "auto". */
export function effectiveSensorType(form, deviceConfig) {
  if (form.sensor_type && form.sensor_type !== "auto") return form.sensor_type;
  return deviceConfig?.effective_sensor_type || "ultrasonic";
}

const round1 = (value) => Math.round(value * 10) / 10;
// Inputs as entered (up to 2 decimals: a radar zero is 2.21, not 2.2); the result to 1 decimal.
const fmt = (value, decimals = 2) =>
  value == null || !Number.isFinite(Number(value)) ? "—" : `${decimals === 1 ? Number(value).toFixed(1) : +Number(value).toFixed(2)} cm`;

const SENSORS = [
  { label: "Sensor 1", calKey: "distance_1_calibration", distKey: "distance_1_calibrated" },
  { label: "Sensor 2", calKey: "distance_2_calibration", distKey: "distance_2_calibrated" },
];

/**
 * The water level worked out step by step from the values on screen, so the arithmetic is visible instead of a
 * formula to apply in your head. rows: [{ label, value, sign }] then the result.
 */
function Calculation({ title, rows, result, note }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="level-calculation">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
      <dl className="space-y-1 text-sm">
        {rows.map(({ label, value, sign }) => (
          <div key={label} className="flex items-baseline justify-between gap-4">
            <dt className="text-slate-600">
              <span className="inline-block w-4 text-slate-400">{sign}</span>
              {label}
            </dt>
            <dd className="tabular-nums text-slate-800">{fmt(value)}</dd>
          </div>
        ))}
        <div className="mt-2 flex items-baseline justify-between gap-4 border-t border-slate-300 pt-2">
          <dt className="font-semibold text-slate-900">
            <span className="inline-block w-4 text-slate-400">=</span>
            {result.label}
          </dt>
          <dd className="font-bold tabular-nums text-slate-900">{fmt(result.value, 1)}</dd>
        </div>
      </dl>
      {note && <p className="mt-2 text-xs text-slate-500">{note}</p>}
    </div>
  );
}

/**
 * A PadiHAT's sensor setup, shared by the Configuration tab and the Dashboard's calibration editor:
 * the sensor type, PadiHAT to ground (every node), then the sleeve overlap and radar zero (radar) or Sensor to Base
 * and the per-sensor bias (ultrasonic), and the water level worked out from them. Pipe opening to ground =
 * PadiHAT to ground + sleeve overlap.
 *
 * Props: form ({sensor_type, height_bottom_to_ground, sleeve_overlap_cm, sensor_to_bottom_cm,
 * distance_1/2_calibration});
 * onChange(patch); deviceConfig (the backend's device_config); latestReading; deviceMac; isStaff;
 * onRadarZeroChanged(device payload); showRadarZero (default true: false when the page shows RadarZeroField itself,
 * because it saves on its own and should not sit among fields that wait for Save).
 */
export default function SensorSetupFields({
  form,
  onChange,
  deviceConfig,
  latestReading,
  deviceMac,
  isStaff,
  onRadarZeroChanged,
  showRadarZero = true,
}) {
  const type = effectiveSensorType(form, deviceConfig);
  const padihat = parseFloat(form.height_bottom_to_ground) || 0;
  const overlap = overlapOf(form);
  const opening = padihat > 0 ? padihat + overlap : 0;
  const savedPadihat = Number(deviceConfig?.height_bottom_to_ground) || 0;
  const benchPadihat = round1(BENCH_PIPE_OPENING_CM - overlap);
  const radarZero = deviceConfig?.radar_zero_cm;
  const number = (key) => (event) => onChange({ [key]: event.target.value === "" ? "" : parseFloat(event.target.value) });
  const detected = deviceConfig?.effective_sensor_type;

  const radarDistance = latestReading?.d1_est ?? latestReading?.distance_1_calibrated;
  const radarLevel =
    radarDistance != null && opening > 0 && radarZero != null ? round1(opening - radarZero - radarDistance) : null;

  return (
    <div className="space-y-5" data-testid="sensor-setup">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field
          label="Sensor type"
          hint={
            form.sensor_type === "auto" || !form.sensor_type
              ? `Detected from the firmware and readings: ${detected || "unknown"}.`
              : "Set by hand."
          }
        >
          {(id, describedBy) => (
            <SelectInput
              id={id}
              describedBy={describedBy}
              value={form.sensor_type || "auto"}
              onChange={(event) => onChange({ sensor_type: event.target.value })}
            >
              {SENSOR_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
          )}
        </Field>

        <div>
          <Field
            label="PadiHAT to ground"
            hint={`From the bottom of the PadiHAT (its sleeve edge) to the field's ground, with the hat on the pipe. On the bench pipe it is ${benchPadihat} cm.`}
            warning={savedPadihat <= 0 ? "Not set yet: a calibration can't start until this is saved." : undefined}
          >
            {(id, describedBy) => (
              <UnitInput
                id={id}
                describedBy={describedBy}
                unit="cm"
                step="0.1"
                value={form.height_bottom_to_ground}
                onChange={number("height_bottom_to_ground")}
              />
            )}
          </Field>
          {savedPadihat <= 0 && padihat <= 0 && (
            <button
              type="button"
              onClick={() => onChange({ height_bottom_to_ground: benchPadihat })}
              className="mt-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100"
            >
              Use {benchPadihat} cm (bench)
            </button>
          )}
        </div>
      </div>

      {type === "radar" ? (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field
              label="Sleeve overlap"
              hint={`How far the sleeve reaches down over the pipe. ${SLEEVE_OVERLAP_DEFAULT_CM} cm is for the PadiHAT radar variation; change it only when the hat design changes.`}
            >
              {(id, describedBy) => (
                <UnitInput
                  id={id}
                  describedBy={describedBy}
                  unit="cm"
                  step="0.1"
                  value={form.sleeve_overlap_cm ?? SLEEVE_OVERLAP_DEFAULT_CM}
                  onChange={number("sleeve_overlap_cm")}
                />
              )}
            </Field>
          </div>

          {showRadarZero && deviceMac && (
            <RadarZeroField deviceMac={deviceMac} isStaff={isStaff} reloadKey={radarZero} onChanged={onRadarZeroChanged} />
          )}

          <Calculation
            title="Water level, worked out"
            rows={[
              { label: "PadiHAT to ground", value: padihat || null, sign: "" },
              { label: "Sleeve overlap", value: overlap, sign: "+" },
              { label: "Radar zero", value: radarZero, sign: "−" },
              { label: "Latest distance", value: radarDistance, sign: "−" },
            ]}
            result={{ label: "Water level", value: radarLevel }}
            note={
              radarZero == null
                ? "No radar zero yet: run a calibration campaign in the Experiment tab."
                : radarDistance == null
                ? "No recent reading to work it out with."
                : undefined
            }
          />
          {opening > 0 && (
            <p className="text-xs text-slate-500" data-testid="pipe-opening">
              Pipe opening to ground: {round1(opening)} cm
            </p>
          )}
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field label="Sensor to Base" hint="From the sensor face to the bottom of the enclosure. Default 3.75 cm.">
              {(id, describedBy) => (
                <UnitInput
                  id={id}
                  describedBy={describedBy}
                  unit="cm"
                  step="0.01"
                  value={form.sensor_to_bottom_cm}
                  onChange={number("sensor_to_bottom_cm")}
                />
              )}
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {SENSORS.map(({ label, calKey, distKey }) => {
              const distance = latestReading?.[distKey];
              const offset = (parseFloat(form.sensor_to_bottom_cm) || 0) + padihat + (parseFloat(form[calKey]) || 0);
              return (
                <div key={label} className="space-y-4">
                  <Field
                    label={`${label} bias correction`}
                    hint="Leave at 0 unless the sensor is always off by the same amount (+ reads too far, − too close)."
                  >
                    {(id, describedBy) => (
                      <UnitInput
                        id={id}
                        describedBy={describedBy}
                        unit="cm"
                        step="0.01"
                        value={form[calKey]}
                        onChange={number(calKey)}
                      />
                    )}
                  </Field>
                  <Calculation
                    title={`${label}, worked out`}
                    rows={[
                      { label: "Sensor to Base", value: parseFloat(form.sensor_to_bottom_cm) || 0, sign: "" },
                      { label: "PadiHAT to ground", value: padihat, sign: "+" },
                      { label: "Bias correction", value: parseFloat(form[calKey]) || 0, sign: "+" },
                      { label: "Latest distance", value: distance, sign: "−" },
                    ]}
                    result={{ label: "Water level", value: distance != null && offset > 0 ? round1(offset - distance) : null }}
                    note={distance == null ? "No recent reading to work it out with." : undefined}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
